/** Programmatic PDF builder for the map export.
 *
 * The page is a full-bleed A4 map (portrait or landscape) with everything else
 * drawn as overlay chips on top, poster-style: a title chip (municipality /
 * mode / date / logo) top-left, a north arrow top-right, a scale bar
 * bottom-left, and the legend box bottom-right. The map image is drawn as a
 * raster; every overlay is vector text/shapes so it stays crisp.
 *
 * Layout constants are in millimetres and shared with the modal (via
 * `getPageSize` / `OVERLAY`) so the on-screen preview is WYSIWYG.
 */

import { haversineMeters } from "./geometry";
import type { ExportLegend } from "./exportLegend";

export type ExportOrientation = "landscape" | "portrait";

const A4_SHORT = 210;
const A4_LONG = 297;

export function getPageSize(orientation: ExportOrientation): { w: number; h: number } {
  return orientation === "landscape"
    ? { w: A4_LONG, h: A4_SHORT }
    : { w: A4_SHORT, h: A4_LONG };
}

/** Shared overlay geometry (mm). The modal scales these by (renderedPx / mm)
 *  so its overlays sit exactly where the printed ones will. */
export const OVERLAY = {
  margin: 6,
  legendW: 54,
  pad: 3,
  /** Extra lift (mm) applied to the legend box bottom edge so it clears the
   *  MapLibre attribution banner that sits at the very bottom-right corner. */
  legendLift: 8,
  // Font sizes in millimetres (converted to pt for jsPDF, to px for the modal).
  titleMm: 4.2,
  subMm: 3,
  entryMm: 3,
  swatchW: 7,
  gap: 2.4,
  lineGap: 1.6,
} as const;

const MM_PER_PT = 25.4 / 72;
const mmToPt = (mm: number) => mm / MM_PER_PT;

export interface ExportPdfParams {
  mapImage: string;
  bounds: { west: number; south: number; east: number; north: number };
  orientation: ExportOrientation;
  legend: ExportLegend;
  municipality: string;
  modeLabel: string;
  logoUrl: string | null;
  labels: {
    /** Heading for the species block — "Species habitat" (habitat mode) or
     *  "Species considered" (planning mode). */
    speciesHeading: string;
    timePeriod: string;
    noWork: string;
    disclaimer: string;
  };
  fileName: string;
}

const INK = { r: 23, g: 63, b: 53 };
const SLATE = { r: 40, g: 58, b: 61 };
const MUTED = { r: 120, g: 132, b: 130 };
const NO_WORK = { r: 220, g: 38, b: 38 };
const WHITE = { r: 255, g: 255, b: 255 };

type RGB = { r: number; g: number; b: number };

function hexToRgb(hex: string): RGB {
  const cleaned = hex.replace("#", "");
  const full = cleaned.length === 3 ? cleaned.split("").map((c) => c + c).join("") : cleaned;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return SLATE;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

async function loadImage(
  url: string,
): Promise<{ dataUrl: string; width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function niceDistance(meters: number): number {
  const pow = Math.pow(10, Math.floor(Math.log10(meters)));
  const frac = meters / pow;
  const nice = frac >= 5 ? 5 : frac >= 2 ? 2 : 1;
  return nice * pow;
}

function formatDistance(meters: number): string {
  return meters >= 1000 ? `${meters / 1000} km` : `${meters} m`;
}

/** A "nice" scale bar for a map of `pageWmm` millimetres spanning `bounds`.
 *  Returns the bar length both in ground metres and page mm, plus a label.
 *  Shared with the modal so its preview scale bar matches the PDF's. */
export function computeScaleBar(
  bounds: { west: number; south: number; east: number; north: number },
  pageWmm: number,
): { meters: number; mm: number; label: string } | null {
  const midLat = (bounds.north + bounds.south) / 2;
  const ground = haversineMeters(bounds.west, midLat, bounds.east, midLat);
  if (!(ground > 0)) return null;
  const metersPerMm = ground / pageWmm;
  const meters = niceDistance(metersPerMm * 35);
  return { meters, mm: meters / metersPerMm, label: formatDistance(meters) };
}

export async function exportMapToPdf(params: ExportPdfParams): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const page = getPageSize(params.orientation);
  const doc = new jsPDF({ orientation: params.orientation, unit: "mm", format: "a4" });

  const setFill = (c: RGB) => doc.setFillColor(c.r, c.g, c.b);
  const setDraw = (c: RGB) => doc.setDrawColor(c.r, c.g, c.b);
  const setText = (c: RGB) => doc.setTextColor(c.r, c.g, c.b);

  // ── Full-bleed map ───────────────────────────────────────
  doc.addImage(params.mapImage, "PNG", 0, 0, page.w, page.h);

  const m = OVERLAY.margin;

  // ── Title chip (top-left): logo · municipality / mode · date ─
  const logo = params.logoUrl ? await loadImage(params.logoUrl) : null;
  {
    const p = OVERLAY.pad;
    const logoH = logo ? 11 : 0;
    const logoW = logo ? (logo.width / logo.height) * logoH : 0;
    const textX0 = p + (logo ? logoW + p : 0);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(mmToPt(OVERLAY.titleMm));
    const title = params.municipality || "—";
    const titleW = doc.getTextWidth(title);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(mmToPt(OVERLAY.subMm));
    const subtitle = `${params.modeLabel}  ·  ${new Date().toISOString().slice(0, 10)}`;
    const subW = doc.getTextWidth(subtitle);

    const textBlockH = OVERLAY.titleMm + OVERLAY.lineGap + OVERLAY.subMm;
    const chipH = Math.max(logoH, textBlockH) + 2 * p;
    const chipW = textX0 + Math.max(titleW, subW) + p;

    setFill(WHITE);
    setDraw({ r: 225, g: 232, b: 229 });
    doc.setLineWidth(0.2);
    doc.roundedRect(m, m, chipW, chipH, 1.5, 1.5, "FD");

    if (logo) {
      doc.addImage(logo.dataUrl, "PNG", m + p, m + (chipH - logoH) / 2, logoW, logoH);
    }
    const textTop = m + (chipH - textBlockH) / 2;
    setText(INK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(mmToPt(OVERLAY.titleMm));
    doc.text(title, m + textX0, textTop + OVERLAY.titleMm);
    setText(MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(mmToPt(OVERLAY.subMm));
    doc.text(subtitle, m + textX0, textTop + OVERLAY.titleMm + OVERLAY.lineGap + OVERLAY.subMm);
  }

  // ── North arrow (top-right) ──────────────────────────────
  {
    const nx = page.w - m - 4;
    const ny = m + 3;
    setFill(WHITE);
    setDraw({ r: 225, g: 232, b: 229 });
    doc.setLineWidth(0.2);
    doc.roundedRect(nx - 5, ny - 2, 10, 13, 1.2, 1.2, "FD");
    setFill(INK);
    doc.triangle(nx, ny, nx - 2.2, ny + 6, nx + 2.2, ny + 6, "F");
    setText(INK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(mmToPt(2.6));
    doc.text("N", nx, ny + 10, { align: "center" });
  }

  // ── Scale bar (bottom-left) ──────────────────────────────
  const scale = computeScaleBar(params.bounds, page.w);
  if (scale) {
    const barMm = scale.mm;
    const sx = m + OVERLAY.pad;
    const sy = page.h - m - 3;
    setFill(WHITE);
    setDraw({ r: 225, g: 232, b: 229 });
    doc.setLineWidth(0.2);
    doc.roundedRect(m, sy - 5.5, barMm + 20, 9, 1.2, 1.2, "FD");
    setDraw(SLATE);
    doc.setLineWidth(0.5);
    doc.line(sx, sy, sx + barMm, sy);
    doc.line(sx, sy - 1.5, sx, sy + 1.5);
    doc.line(sx + barMm, sy - 1.5, sx + barMm, sy + 1.5);
    setText(SLATE);
    doc.setFontSize(mmToPt(2.6));
    doc.text(scale.label, sx + barMm + 2, sy + 0.9);
  }

  // ── Legend box (bottom-right) ────────────────────────────
  drawLegendBox(doc, params, page, { setFill, setDraw, setText, hexToRgb });

  doc.save(params.fileName);
}

/** Draw the bottom-right legend overlay. Split out so the (variable) box height
 *  can be computed up-front from the wrapped line counts, then the panel and
 *  its contents drawn in one pass. */
function drawLegendBox(
  doc: import("jspdf").jsPDF,
  params: ExportPdfParams,
  page: { w: number; h: number },
  paint: {
    setFill: (c: RGB) => void;
    setDraw: (c: RGB) => void;
    setText: (c: RGB) => void;
    hexToRgb: (hex: string) => RGB;
  },
): void {
  const { legend, labels } = params;
  const { margin, legendW, pad, entryMm, swatchW, gap, lineGap } = OVERLAY;
  const innerW = legendW - 2 * pad;
  const textW = innerW - swatchW - gap;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(mmToPt(entryMm));

  // Pre-split every wrapping label so we can size the box before drawing it.
  type Row = {
    kind: "entry" | "fill-entry" | "name-only" | "heading" | "text";
    lines: string[];
    color?: RGB;
    dashed?: boolean;
    opacity?: number;
  };
  const rows: Row[] = [];

  for (const measure of legend.measures) {
    rows.push({ kind: "entry", color: paint.hexToRgb(measure.color), lines: doc.splitTextToSize(measure.label, textW) });
  }
  if (legend.hasNoWork) {
    rows.push({ kind: "entry", color: NO_WORK, dashed: true, lines: doc.splitTextToSize(labels.noWork, textW) });
  }

  if (legend.species.length > 0) {
    rows.push({ kind: "heading", lines: [labels.speciesHeading] });
    for (const s of legend.species) {
      if (s.showColor) {
        rows.push({ kind: "entry", color: paint.hexToRgb(s.color), lines: doc.splitTextToSize(s.label, textW) });
      } else {
        rows.push({ kind: "name-only", lines: doc.splitTextToSize(s.label, innerW) });
      }
    }
  }

  for (const layer of legend.layers) {
    if (layer.fill) {
      rows.push({
        kind: "fill-entry",
        color: paint.hexToRgb(layer.color),
        opacity: layer.opacity,
        lines: doc.splitTextToSize(layer.label, textW),
      });
    } else {
      rows.push({ kind: "entry", color: paint.hexToRgb(layer.color), lines: doc.splitTextToSize(layer.label, textW) });
    }
  }

  if (legend.showTimePeriod) {
    rows.push({ kind: "heading", lines: [labels.timePeriod] });
    rows.push({ kind: "text", lines: doc.splitTextToSize(legend.monthsLabel, innerW) });
  }
  rows.push({ kind: "name-only", lines: doc.splitTextToSize(labels.disclaimer, innerW) });

  const headingH = entryMm + lineGap + 1.5;
  const rowHeight = (row: Row) =>
    row.kind === "heading" ? headingH : row.lines.length * (entryMm + lineGap) + 1;
  const contentH = rows.reduce((sum, row) => sum + rowHeight(row), 0);
  const boxH = contentH + 2 * pad;

  const boxX = page.w - margin - legendW;
  const boxY = page.h - margin - OVERLAY.legendLift - boxH;

  paint.setFill(WHITE);
  paint.setDraw({ r: 225, g: 232, b: 229 });
  doc.setLineWidth(0.2);
  doc.roundedRect(boxX, boxY, legendW, boxH, 1.5, 1.5, "FD");

  let y = boxY + pad + entryMm;
  const x = boxX + pad;

  for (const row of rows) {
    if (row.kind === "heading") {
      paint.setText(INK);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(mmToPt(entryMm));
      doc.text(row.lines[0], x, y);
      y += headingH;
      continue;
    }
    if (row.kind === "text") {
      paint.setText(SLATE);
      doc.setFont("helvetica", "normal");
      doc.text(row.lines, x, y);
      y += row.lines.length * (entryMm + lineGap) + 1;
      continue;
    }
    if (row.kind === "name-only") {
      paint.setText(MUTED);
      doc.setFont("helvetica", "normal");
      doc.text(row.lines, x, y);
      y += row.lines.length * (entryMm + lineGap) + 1;
      continue;
    }
    if (row.kind === "fill-entry" && row.color) {
      // Filled square swatch (polygon layers). Simulate the layer's opacity by
      // blending its colour toward the white legend background so the printed
      // swatch matches the semi-transparent fill on the map.
      const a = Math.max(row.opacity ?? 1, 0.45);
      const eff: RGB = {
        r: Math.round(row.color.r * a + 255 * (1 - a)),
        g: Math.round(row.color.g * a + 255 * (1 - a)),
        b: Math.round(row.color.b * a + 255 * (1 - a)),
      };
      const sq = 3;
      paint.setFill(eff);
      paint.setDraw(row.color);
      doc.setLineWidth(0.2);
      doc.rect(x, y - entryMm / 2 - sq / 2, sq, sq, "FD");
      paint.setText(SLATE);
      doc.setFont("helvetica", "normal");
      doc.text(row.lines, x + swatchW + gap, y);
      y += row.lines.length * (entryMm + lineGap) + 1;
      continue;
    }
    // entry: coloured line swatch + label
    if (row.color) {
      paint.setDraw(row.color);
      doc.setLineWidth(row.dashed ? 1.6 : 1.1);
      if (row.dashed) doc.setLineDashPattern([0.9, 0.7], 0);
      doc.line(x, y - entryMm / 2, x + swatchW, y - entryMm / 2);
      if (row.dashed) doc.setLineDashPattern([], 0);
    }
    paint.setText(SLATE);
    doc.setFont("helvetica", "normal");
    doc.text(row.lines, x + swatchW + gap, y);
    y += row.lines.length * (entryMm + lineGap) + 1;
  }
}
